import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ShieldCheck, Star, Truck, MapPin, ChevronDown } from "lucide-react";
import Seo from "@/components/Seo";
import Marquee from "@/components/Marquee";
import { FadeUp, MaskedLine } from "@/components/Reveal";
import { QuoteButton } from "@/components/Layout";
import { BUSINESS, SERVICES, MANIFESTO, REVIEWS, REVIEWS_RATING, SERVICE_AREAS, IMAGES, WORK_PHOTOS } from "@/content/siteContent";
import ReviewCard from "@/components/ReviewCard";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 140]);

  return (
    <section ref={ref} className="relative overflow-hidden">
      <div className="pointer-events-none absolute -left-40 top-0 h-[600px] w-[600px] rounded-full bg-gold/5 blur-3xl" />
      <div className="mx-auto grid max-w-7xl gap-12 px-4 pb-20 pt-14 sm:px-6 lg:grid-cols-2 lg:items-center lg:pb-28 lg:pt-24">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="eyebrow mb-6 flex items-center gap-3"
          >
            <span className="inline-block h-px w-10 bg-gold" /> Veteran-Owned · {BUSINESS.region}
          </motion.p>
          <h1 data-testid="hero-title" className="font-display text-5xl font-extrabold uppercase leading-[0.95] tracking-tight text-white sm:text-6xl lg:text-7xl">
            <MaskedLine delay={0.15}>Moving with</MaskedLine>
            <MaskedLine delay={0.3}>honor. Hauling</MaskedLine>
            <MaskedLine delay={0.45}><span className="text-gold">with precision.</span></MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="mt-6 max-w-md text-base leading-relaxed text-slate-300 sm:text-lg"
          >
            From full-service residential moves to estate cleanouts and junk removal, our veteran-led crew brings military discipline to every job across Southern California.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <QuoteButton testId="hero-request-quote-btn" className="px-9 py-4 text-base" />
            <Link
              to="/services"
              data-testid="hero-services-btn"
              className="inline-flex items-center gap-2 border border-white/20 px-9 py-4 font-display text-base font-bold uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:border-gold hover:text-gold"
            >
              Explore Services <ArrowRight className="h-4 w-4" />
            </Link>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.1 }}
            className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-xs font-semibold uppercase tracking-[0.2em] text-slate-400"
          >
            <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-gold" /> Licensed &amp; Insured</span>
            <span className="flex items-center gap-2"><Star className="h-4 w-4 text-gold" fill="currentColor" /> Veteran Owned</span>
            <span className="flex items-center gap-2"><Truck className="h-4 w-4 text-gold" /> 13 Services</span>
          </motion.div>
        </div>
        <motion.div style={{ y }} className="relative hidden lg:block">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="gold-frame"
          >
            <img
              src={IMAGES.hero}
              alt="Home Team Moving Co. truck and crew on a Southern California job"
              className="h-[540px] w-full object-cover"
              loading="eager"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="absolute -bottom-8 -left-8 border border-white/10 bg-navy-900/90 px-6 py-5 backdrop-blur-xl"
          >
            <p className="flex items-center gap-2 font-display text-2xl font-bold uppercase text-white">
              <Star className="h-5 w-5 text-gold" fill="currentColor" /> {REVIEWS_RATING} on Facebook
            </p>
            <p className="mt-1 text-xs uppercase tracking-[0.2em] text-slate-400">Trusted by SoCal families &amp; businesses</p>
          </motion.div>
        </motion.div>
      </div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 lg:block"
        aria-hidden="true"
      >
        <ChevronDown className="h-5 w-5 animate-bounce text-gold/70" />
      </motion.div>
    </section>
  );
};

const Manifesto = () => (
  <section className="border-b border-white/10">
    <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:py-28">
      <FadeUp>
        <p className="eyebrow mb-4">The Home Team Standard</p>
        <h2 className="max-w-2xl font-display text-3xl font-bold uppercase tracking-tight text-white sm:text-4xl lg:text-5xl">
          Service forged by service
        </h2>
      </FadeUp>
      <div className="mt-14 grid gap-px border border-white/10 bg-white/10 md:grid-cols-3">
        {MANIFESTO.map((m, i) => (
          <FadeUp key={m.chapter} delay={i * 0.12} className="bg-navy-950 p-8 lg:p-10">
            <p className="text-outline font-display text-6xl font-extrabold leading-none">{m.chapter}</p>
            <h3 className="mt-6 font-display text-xl font-bold uppercase tracking-wide text-gold">{m.title}</h3>
            <p className="mt-3 text-sm leading-relaxed text-slate-300">{m.content}</p>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

const ServicesPreview = () => (
  <section className="border-b border-white/10 bg-navy-900/40">
    <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:py-28">
      <FadeUp className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="eyebrow mb-4">What We Do</p>
          <h2 className="font-display text-3xl font-bold uppercase tracking-tight text-white sm:text-4xl lg:text-5xl">
            13 ways we move you forward
          </h2>
        </div>
        <Link to="/services" data-testid="view-all-services-link" className="group inline-flex items-center gap-2 font-display text-sm font-bold uppercase tracking-[0.2em] text-gold">
          All services <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </FadeUp>
      <div data-testid="services-grid" className="mt-14 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
        {SERVICES.slice(0, 6).map((s, i) => (
          <FadeUp key={s.id} delay={i * 0.08} className="group bg-navy-950 p-8 transition-colors duration-300 hover:bg-navy-800">
            <p className="font-display text-sm font-bold tracking-[0.3em] text-gold/60">{String(i + 1).padStart(2, "0")}</p>
            <h3 className="mt-4 font-display text-xl font-bold uppercase tracking-wide text-white">{s.title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-400">{s.description}</p>
            <Link to={`/quote?service=${s.id}`} data-testid={`service-quote-link-${s.id}`} className="mt-5 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-gold opacity-0 transition-opacity duration-300 group-hover:opacity-100">
              Get a quote <ArrowRight className="h-3 w-3" />
            </Link>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

const TrustSplit = () => (
  <section className="border-b border-white/10">
    <div className="mx-auto grid max-w-7xl gap-12 px-4 py-20 sm:px-6 lg:grid-cols-2 lg:items-center lg:py-28">
      <FadeUp className="gold-frame order-2 lg:order-1">
        <img src={IMAGES.boxes} alt="A bedroom set placed and assembled by the Home Team crew" className="h-[420px] w-full object-cover" loading="lazy" />
      </FadeUp>
      <FadeUp delay={0.15} className="order-1 lg:order-2">
        <p className="eyebrow mb-4">Why Home Team</p>
        <h2 className="font-display text-3xl font-bold uppercase tracking-tight text-white sm:text-4xl">
          The crew you want on your six
        </h2>
        <p className="mt-5 leading-relaxed text-slate-300">
          Anyone can rent a truck. What sets us apart is how we show up: on time, in uniform, with a plan. Every job is scoped, scheduled, and executed with the accountability we learned in uniform.
        </p>
        <ul className="mt-8 space-y-4">
          {["Upfront, honest estimates — no surprise fees", "Trained, background-checked veteran-led crews", "Photos & video quotes for fast, accurate pricing", "Donation-first hauling that keeps goods in the community"].map((item) => (
            <li key={item} className="flex items-start gap-3 text-sm text-slate-200">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-gold" /> {item}
            </li>
          ))}
        </ul>
        <div className="mt-9">
          <QuoteButton testId="trust-quote-cta" />
        </div>
      </FadeUp>
    </div>
  </section>
);

const RecentWork = () => (
  <section data-testid="recent-work-section" className="border-b border-white/10">
    <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:py-28">
      <FadeUp>
        <p className="eyebrow mb-4">Recent Work</p>
        <h2 className="max-w-2xl font-display text-3xl font-bold uppercase tracking-tight text-white sm:text-4xl lg:text-5xl">
          Straight from the job site
        </h2>
      </FadeUp>
      <div className="mt-14 grid auto-rows-[160px] grid-cols-2 gap-3 sm:auto-rows-[220px] sm:gap-4 lg:grid-cols-4">
        {WORK_PHOTOS.map((p, i) => (
          <FadeUp
            key={p.src}
            delay={i * 0.07}
            className={`group relative h-full overflow-hidden border border-white/10 ${i === 0 ? "col-span-2 row-span-2" : ""} ${i === WORK_PHOTOS.length - 1 ? "col-span-2" : ""}`}
          >
            <img
              src={p.src}
              alt={p.alt}
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-navy-950/95 to-transparent px-4 pb-3 pt-10 font-display text-xs font-bold uppercase tracking-[0.2em] text-gold opacity-0 transition-opacity duration-300 group-hover:opacity-100">
              {p.label}
            </span>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

const ReviewsTeaser = () => (
  <section data-testid="reviews-section" className="border-b border-white/10 bg-navy-900/40">
    <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:py-28">
      <FadeUp className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="eyebrow mb-4">Word on the Street</p>
          <h2 className="font-display text-3xl font-bold uppercase tracking-tight text-white sm:text-4xl lg:text-5xl">
            Neighbors who'd call us again
          </h2>
        </div>
        <Link to="/reviews" className="group inline-flex items-center gap-2 font-display text-sm font-bold uppercase tracking-[0.2em] text-gold">
          All reviews <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </FadeUp>
      <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {REVIEWS.map((r, i) => (
          <FadeUp key={r.name} delay={i * 0.1} className="h-full">
            <ReviewCard review={r} />
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

const ServiceArea = () => (
  <section className="border-b border-white/10">
    <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:py-28">
      <FadeUp className="max-w-2xl">
        <p className="eyebrow mb-4">Service Area</p>
        <h2 className="font-display text-3xl font-bold uppercase tracking-tight text-white sm:text-4xl lg:text-5xl">
          Proudly serving Southern California
        </h2>
        <p className="mt-5 leading-relaxed text-slate-300">
          Our crews roll out across the region every day. Don't see your city listed? Reach out — if you're in SoCal, chances are we've got you covered.
        </p>
      </FadeUp>
      <div data-testid="service-area-map" className="mt-10 flex flex-wrap gap-3">
        {SERVICE_AREAS.map((area, i) => (
          <FadeUp key={area} delay={i * 0.06}>
            <span className="inline-flex items-center gap-2 border border-white/10 bg-navy-900 px-5 py-3 font-display text-sm font-semibold uppercase tracking-[0.15em] text-slate-200 transition-colors duration-300 hover:border-gold hover:text-gold">
              <MapPin className="h-4 w-4 text-gold" /> {area}
            </span>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

const CtaBand = () => (
  <section className="relative overflow-hidden">
    <img src={IMAGES.truckCity} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-20" loading="lazy" />
    <div className="absolute inset-0 bg-gradient-to-r from-navy-950 via-navy-950/80 to-navy-950/40" />
    <div className="relative mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:py-32">
      <FadeUp>
        <h2 className="max-w-3xl font-display text-4xl font-extrabold uppercase leading-none tracking-tight text-white sm:text-5xl lg:text-6xl">
          Ready when <span className="text-gold">you are.</span>
        </h2>
        <p className="mt-5 max-w-xl text-slate-300">
          Send us a few photos of the job and get a fast, honest estimate — no obligation, no pressure.
        </p>
        <div className="mt-9 flex flex-wrap gap-4">
          <QuoteButton testId="cta-band-quote-btn" className="px-9 py-4 text-base" />
          <a href={BUSINESS.phoneHref} data-testid="cta-band-call-btn" className="inline-flex items-center gap-2 border border-white/20 px-9 py-4 font-display text-base font-bold uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:border-gold hover:text-gold">
            Call {BUSINESS.phone}
          </a>
        </div>
      </FadeUp>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Seo
        title="Home Team Moving Co. | Veteran-Owned Moving & Hauling — Southern California"
        siteName="Home Team Moving Co."
        description="Veteran-owned moving, hauling, cleanouts, and junk removal across Southern California. Request a fast photo quote today."
        image="/logo.png"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "MovingCompany",
          name: BUSINESS.name,
          email: BUSINESS.email,
          areaServed: BUSINESS.region,
          description: "Veteran-owned moving and hauling company serving Southern California.",
          url: window.location.origin,
        }}
      />
      <Hero />
      <Marquee />
      <Manifesto />
      <ServicesPreview />
      <TrustSplit />
      <RecentWork />
      <ReviewsTeaser />
      <ServiceArea />
      <CtaBand />
    </>
  );
}
